<template>
  <div class="common-pagination">
    <el-pagination
      v-model:current-page="currentPageModel"
      v-model:page-size="pageSizeModel"
      :total="total"
      :page-sizes="pageSizes"
      :layout="layout"
      :pager-count="pagerCount"
      :background="background"
      :hide-on-single-page="hideOnSinglePage"
      :small="small"
      :disabled="disabled"
      :prev-text="prevText"
      :next-text="nextText"
      @size-change="handleSizeChange"
      @current-change="handleCurrentChange"
    />
  </div>
</template>

<script>
import { computed } from 'vue'

export default {
  name: 'CommonPagination',
  props: {
    currentPage: {
      type: Number,
      default: 1
    },
    pageSize: {
      type: Number,
      default: 10
    },
    total: {
      type: Number,
      default: 0
    },
    pageSizes: {
      type: Array,
      default: () => [10, 20, 50, 100]
    },
    layout: {
      type: String,
      default: 'prev, pager, next, jumper'
    },
    pagerCount: {
      type: Number,
      default: 7
    },
    background: {
      type: Boolean,
      default: true
    },
    hideOnSinglePage: {
      type: Boolean,
      default: false
    },
    small: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    prevText: {
      type: String,
      default: '上一页'
    },
    nextText: {
      type: String,
      default: '下一页'
    }
  },
  emits: [
    'update:currentPage',
    'update:pageSize',
    'size-change',
    'current-change'
  ],
  setup(props, { emit }) {
    const currentPageModel = computed({
      get: () => props.currentPage,
      set: (value) => emit('update:currentPage', value)
    })

    const pageSizeModel = computed({
      get: () => props.pageSize,
      set: (value) => emit('update:pageSize', value)
    })

    const handleSizeChange = (value) => {
      emit('size-change', value)
    }

    const handleCurrentChange = (value) => {
      emit('current-change', value)
    }

    return {
      currentPageModel,
      pageSizeModel,
      handleSizeChange,
      handleCurrentChange
    }
  }
}
</script>

<style lang="scss" scoped>
.common-pagination {
  width: 100%;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e8edf3;
  display: flex;
  justify-content: flex-end;
}

.common-pagination :deep(.el-pagination) {
  --el-pagination-button-color: #2b3f57;
  --el-pagination-hover-color: #1677ff;
  --el-pagination-button-bg-color: #ffffff;
  --el-pagination-button-disabled-bg-color: #eef3f9;
  gap: 8px 10px;
  width: 100%;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: center;
  background: transparent;
  box-shadow: none;
}

.common-pagination :deep(.el-pagination__jump) {
  margin: 0;
  color: #5b6b81;
  font-size: 13px;
}

.common-pagination :deep(.el-pagination__jump) {
  display: inline-flex;
  align-items: center;
}

.common-pagination :deep(.btn-prev),
.common-pagination :deep(.btn-next),
.common-pagination :deep(.el-pager li) {
  min-width: 36px;
  height: 36px;
  margin: 0;
  border: 1px solid #d7e2ef;
  border-radius: 6px;
  background: #ffffff;
  font-weight: 600;
  transition: all 0.2s ease;
}

.common-pagination :deep(.btn-prev),
.common-pagination :deep(.btn-next) {
  min-width: 72px;
  padding: 0 14px;
}

.common-pagination :deep(.btn-prev:hover),
.common-pagination :deep(.btn-next:hover),
.common-pagination :deep(.el-pager li:hover) {
  color: #1677ff;
  border-color: #9fc8ff;
}

.common-pagination :deep(.el-pager li.is-active) {
  color: #ffffff;
  border-color: #1677ff;
  background: #1677ff;
}

.common-pagination :deep(.btn-prev:disabled),
.common-pagination :deep(.btn-next:disabled) {
  opacity: 0.7;
}

.common-pagination :deep(.el-select .el-input__wrapper),
.common-pagination :deep(.el-input__wrapper) {
  min-height: 38px;
  border-radius: 10px;
  background: #ffffff;
  box-shadow: 0 0 0 1px #d7e2ef inset;
}

.common-pagination :deep(.el-select .el-input__wrapper:hover),
.common-pagination :deep(.el-input__wrapper:hover) {
  box-shadow: 0 0 0 1px #9fc8ff inset;
}

.common-pagination :deep(.el-select .el-input__wrapper.is-focus),
.common-pagination :deep(.el-input__wrapper.is-focus) {
  box-shadow: 0 0 0 1px #1677ff inset;
}

@media (max-width: 768px) {
  .common-pagination {
    justify-content: center;
  }

  .common-pagination :deep(.el-pagination) {
    justify-content: center;
  }
}

@media (max-width: 640px) {
  .common-pagination :deep(.btn-prev),
  .common-pagination :deep(.btn-next) {
    min-width: 38px;
    padding: 0 10px;
    font-size: 0;
  }

  .common-pagination :deep(.btn-prev::before) {
    content: '上';
    font-size: 13px;
  }

  .common-pagination :deep(.btn-next::before) {
    content: '下';
    font-size: 13px;
  }
}
</style>
